/**
 * ABOUTME: The "go look at the thing the agent just made" affordance.
 * Backs the turn-closing "Open Content page" button driven by
 * `run_content_summary` and the generated-image card. Navigates with a react-router
 * <Link> to an internal `/content/...` path — never a reload.
 *
 * It generalizes the ROUTING pattern from VideoAssetProgressCard, not its
 * styling: that one is a ~16px `text-xs` inline link, which is unusable with a
 * thumb. This is a full-width, ≥44px-high touch target on every viewport.
 */
import { ArrowRight, FileText, LayoutGrid } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import type { ContentSummaryItem } from '@/stores/aiChatTypes'

interface ContentLinkButtonProps {
  /** Internal path to navigate to (a `deep_link_path`, or `/content`). */
  to: string
  /** Call-to-action line. */
  label: string
  /** Heading line above the CTA; omitted when blank. */
  title?: string
  entityKind?: ContentSummaryItem['entity_kind']
  platforms?: string[]
  thumbUrl?: string | null
  'data-testid'?: string
}

/** Fallback heading when the backend sends an empty `title` (it may). */
function kindLabel(entityKind: ContentSummaryItem['entity_kind'] | undefined): string {
  return entityKind === 'post' ? 'New post' : ''
}

export function ContentLinkButton({
  to,
  label,
  title,
  entityKind,
  platforms,
  thumbUrl,
  'data-testid': testId = 'content-link-button',
}: ContentLinkButtonProps) {
  const heading = title?.trim() || kindLabel(entityKind)
  // No kind = the turn-level summary button, which points at the whole grid
  // rather than one entity.
  const KindIcon = entityKind === 'post' ? FileText : LayoutGrid

  return (
    <Button
      asChild
      variant="outline"
      size="touch"
      // `h-auto min-h-11` keeps the 44px floor while letting a two-line
      // title/CTA stack grow instead of clipping at 390px.
      className="my-1 h-auto min-h-11 w-full justify-start gap-2 px-3 py-2"
    >
      <Link to={to} data-testid={testId}>
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="size-8 shrink-0 rounded object-cover"
            loading="lazy"
          />
        ) : (
          <KindIcon className="text-muted-foreground size-4 shrink-0" />
        )}

        <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
          {heading && <span className="w-full truncate text-xs font-medium">{heading}</span>}
          <span className="text-primary text-xs font-medium">{label}</span>
          {platforms && platforms.length > 0 && (
            <span className="text-muted-foreground w-full truncate text-[0.625rem] capitalize">
              {platforms.join(' · ')}
            </span>
          )}
        </span>

        <ArrowRight className="size-4 shrink-0" />
      </Link>
    </Button>
  )
}
